import { BodyRegular, Card, FlexColMd, FlexMd, FlexSpaceBetween, Headline2 } from 'components/Elements';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import React from 'react';
import { styled } from 'styled-components';
import strings from '../../../../strings/strings.json';
import ReviewGeneratedAudioComponent from '../generateContentComponents/reviewGeneratedContentComponents/ReviewGeneratedAudioComponent';
import ReviewGeneratedTextComponent from '../generateContentComponents/reviewGeneratedContentComponents/ReviewGeneratedTextComponent';
import { GenerateType } from 'models/Activity';
import Notification, { NotificationType } from 'components/notifiers/Notification';
import ReviewGeneratedModuleQuestionnaire from '../generateContentComponents/reviewGeneratedContentComponents/ReviewGeneratedModuleQuestionnaire';
import Hint from 'components/Hint';
import { ModuleListItem } from '../generateContentComponents/ModuleListItem';
import useModifyRequest from 'hooks/useModifyRequest';
import { ErrorNotifierSlim } from 'components/notifiers/ErrorNotifierSlim';
import Button from 'components/buttons/Button';

export default function CourseGenerateContentComponent(): JSX.Element {
    const course = React.useContext(BuildCourseContext).course!;
    const {
        showIncompleteStepsWarning,
        generateContentStatusesState: moduleItems,
        refetchGenerateContentStatuses,
        setActiveStep,
    } = React.useContext(BuildCourseContext);

    const [taskIdToReview, setTaskIdToReview] = React.useState<string>('');
    const [generateAllContentError, setGenerateAllContentError] = React.useState<boolean>(false);
    const [initialLoad, setInitialLoad] = React.useState<boolean>(true);

    const { modifyData: generateAllContent, loading: loadingGenerateAll } = useModifyRequest<any, boolean>(
        `courses/${course.id}/generate-content`,
        'POST',
    );

    const intervalRef = React.useRef<NodeJS.Timeout | null>(null);

    React.useEffect(() => {
        if (!initialLoad) {
            refetchGenerateContentStatuses();
            setInitialLoad(false);
            return;
        }

        const someItemsAreGenerating = moduleItems.some((item) =>
            item.generateItems.some((generateItem) => generateItem.status === 'STARTED'),
        );

        if (someItemsAreGenerating && !intervalRef.current) {
            // Start polling
            intervalRef.current = setInterval(() => {
                refetchGenerateContentStatuses();
            }, 8000);
        }
        if (!someItemsAreGenerating && intervalRef.current) {
            // Stop polling
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [moduleItems, initialLoad]); // eslint-disable-line

    if (!moduleItems) return <></>;
    const nothingToGenerate = moduleItems.flatMap((module) => module.generateItems).length === 0;

    if (nothingToGenerate) {
        return (
            <NoContentContainer>
                <Headline2>{strings.courseEditorPage.steps.aiGeneratedContent.title}</Headline2>
                <Card>
                    <FlexColMd>
                        <BodyRegular $bold>
                            {strings.courseAIGeneratedContentComponent.noContentGenerated.text}
                        </BodyRegular>
                        <Hint hint={strings.courseAIGeneratedContentComponent.noContentGenerated.hintText} />
                        <Button
                            title={strings.courseAIGeneratedContentComponent.noContentGenerated.button.title}
                            aria={strings.courseAIGeneratedContentComponent.noContentGenerated.button.aria}
                            fullwidth
                            onClick={() => setActiveStep('courseStructureAndContent')}
                            autoFocus
                        />
                    </FlexColMd>
                </Card>
            </NoContentContainer>
        );
    }

    if (taskIdToReview) {
        const taskContent = moduleItems
            .flatMap((module) => module.generateItems)
            .find((task) => task.id === taskIdToReview);

        if (taskContent) {
            switch (taskContent.generateType) {
                case GenerateType.TEXT_TO_AUDIO:
                    return (
                        <ReviewGeneratedAudioComponent item={taskContent} backButton={() => setTaskIdToReview('')} />
                    );
                case GenerateType.AUDIO_TO_TEXT:
                case GenerateType.VIDEO_TO_TEXT:
                    return (
                        <ReviewGeneratedTextComponent item={taskContent} backButton={() => setTaskIdToReview('')} />
                    );
                case GenerateType.MODULE_QUESTIONNAIRE:
                    return (
                        <ReviewGeneratedModuleQuestionnaire
                            item={taskContent}
                            backButton={() => setTaskIdToReview('')}
                        />
                    );
                default:
                    return <></>;
            }
        }
    }

    const enableGenerateAllButton = moduleItems.some((item) =>
        item.generateItems.some((generateItem) => generateItem.status === 'NOT_STARTED'),
    );

    const generateAll = async () => {
        setGenerateAllContentError(false);

        const response = await generateAllContent({});
        if (response.errors) {
            setGenerateAllContentError(true);
        } else {
            await refetchGenerateContentStatuses();
        }
    };

    const moduleListItems: JSX.Element[] = moduleItems
        .filter((m) => m.generateItems.length > 0)
        .map((module, moduleIndex) => {
            return <ModuleListItem key={moduleIndex} module={module} setTaskIdToReview={setTaskIdToReview} />;
        });

    return (
        <FlexColMd>
            <HeaderAndGenerateAllButtonRow>
                <Headline2>{strings.courseEditorPage.steps.aiGeneratedContent.title}</Headline2>
                <FlexMd>
                    {generateAllContentError && (
                        <ErrorNotifierSlim description={strings.courseEditorPage.generateAllContentButton.error} />
                    )}
                    <Button
                        title={strings.courseEditorPage.generateAllContentButton.title}
                        aria={strings.courseEditorPage.generateAllContentButton.aria}
                        disabled={!enableGenerateAllButton}
                        submitting={loadingGenerateAll}
                        onClick={generateAll}
                    />
                </FlexMd>
            </HeaderAndGenerateAllButtonRow>
            {showIncompleteStepsWarning && (
                <Notification
                    title={strings.courseEditorPage.generatedContentToBeReviewed.title}
                    notificationType={NotificationType.WARNING}
                    maxWidth="100%"
                />
            )}
            <Hint hint={strings.courseAIGeneratedContentComponent.hint} />
            <FlexColMd>{moduleListItems}</FlexColMd>
        </FlexColMd>
    );
}

const HeaderAndGenerateAllButtonRow = styled(FlexSpaceBetween)`
    width: 100%;
`;

const NoContentContainer = styled(FlexColMd)`
    max-width: 600px;
`;
